import { DestroyRef, inject, Injectable, signal, type Signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';

export interface ShellChromeRouteData {
  /** Present only on public pages that render without workspace navigation. */
  readonly hidden?: true;
}

export const SHELL_CHROME_ROUTE_DATA_KEY = 'shellChrome';

/** Whether the active route renders the authenticated workspace chrome. */
@Injectable({ providedIn: 'root' })
export class ShellChromeVisibilityService {
  private readonly router = inject(Router);
  private readonly visibleState = signal(false);

  readonly visible: Signal<boolean> = this.visibleState.asReadonly();

  constructor() {
    if (this.router.navigated) this.recompute();
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(inject(DestroyRef)),
      )
      .subscribe(() => this.recompute());
  }

  private recompute(): void {
    let route: ActivatedRoute | null = this.router.routerState.root;
    let hidden = false;
    while (route) {
      const data = route.routeConfig?.data?.[SHELL_CHROME_ROUTE_DATA_KEY] as
        | ShellChromeRouteData
        | undefined;
      if (data?.hidden === true) hidden = true;
      route = route.firstChild;
    }
    this.visibleState.set(!hidden);
  }
}